import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { CheckCircle, Crown, X, Sparkle, Infinity as InfinityIcon, ChatCircleDots } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

export function UpgradeModal({ open, onClose, subscription, onPurchase }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCheckout = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/payments/checkout/session`, {
        package_id: 'pro_monthly',
        origin_url: window.location.origin
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        setError('Unable to start checkout. Try again.');
        setLoading(false);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Checkout failed. Try again.');
      setLoading(false);
    }
  };

  if (!open) return null;
  const isPro = subscription?.is_pro;
  const price = subscription?.price_usd || 9.99;
  const duration = subscription?.duration_days || 30;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={onClose}
        data-testid="upgrade-overlay"
      >
        <motion.div
          initial={{ y: 20, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 20, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md rounded-lg overflow-hidden"
          style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}
          data-testid="upgrade-modal"
        >
          {/* Header */}
          <div className="p-6 text-white relative" style={{ backgroundColor: 'var(--primary)' }}>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 opacity-80 hover:opacity-100"
              data-testid="upgrade-close-btn"
            >
              <X size={20} weight="bold" />
            </button>
            <div className="flex items-center gap-3">
              <Crown size={32} weight="fill" />
              <div>
                <div className="font-display text-2xl font-black">FMAA Pro</div>
                <div className="text-sm opacity-90">Unlimited practice + AI Tutor</div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 space-y-5">
            {isPro ? (
              <div className="text-center py-4 space-y-2">
                <CheckCircle size={40} weight="fill" style={{ color: 'var(--success)' }} className="mx-auto" />
                <div className="font-display text-xl font-bold">You're Pro!</div>
                <div className="text-sm opacity-70">
                  Renews {subscription?.pro_expires_at ? new Date(subscription.pro_expires_at).toLocaleDateString() : 'soon'}
                </div>
                <button
                  onClick={handleCheckout}
                  disabled={loading}
                  className="btn-primary w-full py-3 mt-3"
                  data-testid="extend-pro-btn"
                >
                  {loading ? 'Starting checkout…' : `Extend ${duration} more days — $${price}`}
                </button>
              </div>
            ) : (
              <>
                <div className="text-center">
                  <div className="font-display text-5xl font-black" style={{ color: 'var(--text-primary)' }}>
                    ${price}
                  </div>
                  <div className="text-sm opacity-70 font-mono">per {duration} days</div>
                </div>

                <div className="space-y-3 pt-2">
                  <Benefit icon={<InfinityIcon size={18} weight="bold" />} text="Unlimited quiz generations" />
                  <Benefit icon={<ChatCircleDots size={18} weight="fill" />} text="AI Accounting Tutor (Claude 4.5)" />
                  <Benefit icon={<Sparkle size={18} weight="fill" />} text="Priority access to new chapters" />
                  <Benefit icon={<CheckCircle size={18} weight="fill" />} text="Cancel anytime — no renewal lock-in" />
                </div>

                {subscription && subscription.free_quizzes_remaining !== null && (
                  <div className="text-xs opacity-60 text-center font-mono pt-1">
                    Free tier: {subscription.free_quizzes_used_last_30d}/{subscription.free_quiz_quota} quizzes used this month
                  </div>
                )}

                {error && (
                  <div className="p-3 rounded text-sm" style={{ backgroundColor: '#8B000010', color: '#8B0000', border: '1px solid #8B0000' }}>
                    {error}
                  </div>
                )}

                <button
                  onClick={handleCheckout}
                  disabled={loading}
                  className="btn-primary w-full py-4 flex items-center justify-center gap-2"
                  data-testid="upgrade-checkout-btn"
                >
                  {loading ? 'Starting checkout…' : (
                    <>
                      <Crown size={20} weight="fill" />
                      Upgrade to Pro
                    </>
                  )}
                </button>
                <div className="text-xs opacity-60 text-center">
                  Secure payment by Stripe. Test mode.
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Benefit({ icon, text }) {
  return (
    <div className="flex items-center gap-3">
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: 'var(--surface-secondary)', color: 'var(--primary)' }}
      >
        {icon}
      </div>
      <div className="text-sm" style={{ color: 'var(--text-primary)' }}>{text}</div>
    </div>
  );
}

export function UpgradeButton({ isPro, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`hidden sm:flex items-center gap-2 px-3 py-2 rounded text-xs font-mono font-semibold transition-all ${
        isPro ? '' : 'hover:scale-105'
      }`}
      style={{
        backgroundColor: isPro ? 'var(--success)' : 'var(--primary)',
        color: 'white'
      }}
      data-testid="header-upgrade-btn"
    >
      <Crown size={14} weight="fill" />
      {isPro ? 'Pro' : 'Upgrade'}
    </button>
  );
}

/**
 * Polls payment status after returning from Stripe checkout.
 * Returns { status: 'success'|'pending'|'failed'|'expired', data } once resolved or times out.
 */
export function usePaymentPolling(onSuccess) {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const payment = params.get('payment');
    if (!sessionId || payment !== 'success') return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 8;
    const interval = 2000;

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          onSuccess && onSuccess(res.data);
          // Clean URL
          window.history.replaceState({}, '', window.location.pathname);
          return;
        }
        if (res.data.status === 'expired') return;
      } catch (e) {
        // ignore and retry
      }
      attempts += 1;
      if (attempts < maxAttempts) setTimeout(poll, interval);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
